import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { compressImage } from '../lib/imageCompression';
import { UserPlus, Camera, X } from 'lucide-react';

const TEAM_COLORS = [
  '#6366f1', '#ec4899', '#14b8a6', '#f59e0b',
  '#ef4444', '#8b5cf6', '#10b981', '#3b82f6',
];

interface TeamJoinPageProps {
  categoryId: string;
}

export function TeamJoinPage({ categoryId }: TeamJoinPageProps) {
  const [categoryName, setCategoryName] = useState<string | null>(null);
  const [appLogo, setAppLogo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [photo, setPhoto] = useState('');
  const photoRef = useRef<HTMLInputElement>(null);

  // Custom fields
  const [customFieldDefs, setCustomFieldDefs] = useState<any[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      // Get an anonymous auth session so RLS policies (TO authenticated) pass
      await supabase.auth.signInAnonymously();

      const [{ data: cat }, { data: gs }] = await Promise.all([
        supabase.from('categories').select('name, org_id').eq('id', categoryId).single(),
        supabase.from('global_settings').select('app_logo_url').limit(1).single(),
      ]);
      if (cat) {
        setCategoryName(cat.name);
        setOrgId(cat.org_id || null);
        // Load custom field definitions for this organization
        if (cat.org_id) {
          const { data: defs } = await supabase
            .from('custom_field_definitions')
            .select('*')
            .eq('org_id', cat.org_id)
            .order('sort_order');
          if (defs && defs.length > 0) {
            setCustomFieldDefs(defs);
          }
        }
      }
      if (gs?.app_logo_url) setAppLogo(gs.app_logo_url);
      setLoading(false);
    }
    load();
  }, [categoryId]);

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setPhoto(compressed);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Le nom complet est requis.'); return; }
    if (!phone.trim()) { setError('Le numéro de téléphone est requis.'); return; }
    // Validate required custom fields
    for (const field of customFieldDefs) {
      if (field.required && !(customFieldValues[field.id] || '').trim()) {
        setError(`Le champ "${field.label}" est requis.`);
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    // Bloquer si cet email appartient déjà à un membre validé de ce projet
    if (email.trim() && orgId) {
      const emailPattern = email.trim().replace(/[\\%_]/g, '\\$&');
      const { data: existing } = await supabase
        .from('staff')
        .select('id, name')
        .ilike('email', emailPattern)
        .eq('org_id', orgId)
        .eq('status', 'approved')
        .is('deleted_at', null)
        .limit(1);
      if (existing && existing.length > 0) {
        setError(`Vous êtes déjà membre de ce projet (${existing[0].name}). Aucune nouvelle demande n'a été envoyée.`);
        setSubmitting(false);
        return;
      }
    }
    const id = uuidv4();
    const color = TEAM_COLORS[Math.floor(Math.random() * TEAM_COLORS.length)];
    const { error: dbErr } = await supabase.from('staff').insert({
      id,
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim() || null,
      photo_url: photo || null,
      category_id: categoryId,
      org_id: orgId,
      color,
      status: 'pending',
      role: 'staff',
      roles: [],
    });
    if (dbErr) {
      setError("Erreur lors de l'inscription : " + dbErr.message);
    } else {
      // Insert custom field values
      if (customFieldDefs.length > 0) {
        const fieldValueInserts = customFieldDefs
          .filter((f: any) => customFieldValues[f.id] !== undefined && customFieldValues[f.id] !== '')
          .map((f: any) => ({
            id: uuidv4(),
            field_id: f.id,
            staff_id: id,
            value: customFieldValues[f.id] || null,
          }));
        if (fieldValueInserts.length > 0) {
          await supabase.from('staff_custom_field_values').insert(fieldValueInserts);
        }
      }
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!categoryName) {
    return (
      <div className="min-h-screen bg-[#0A0B0E] flex items-center justify-center p-6">
        <div className="text-center bg-[#14161B] border border-red-500/30 rounded-2xl p-10 max-w-sm">
          <div className="text-5xl mb-4">🔗</div>
          <h2 className="text-xl font-bold text-red-400 mb-2">Lien invalide</h2>
          <p className="text-slate-400 text-sm">Ce lien d'invitation n'est plus valide ou a expiré.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B0E] flex flex-col items-center justify-center p-4 font-sans">
      {/* Background glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[15%] left-[5%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[130px]" />
        <div className="absolute bottom-[15%] right-[5%] w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[130px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          {appLogo ? (
            <img src={appLogo} alt="Logo" className="h-14 object-contain" />
          ) : (
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center text-2xl shadow-xl">🎪</div>
          )}
        </div>

        {submitted ? (
          /* ── Success state ────────────────────────────────────── */
          <div className="bg-[#14161B] border border-emerald-500/30 rounded-2xl p-10 text-center shadow-2xl">
            <div className="w-20 h-20 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-5 border border-emerald-500/30">
              <span className="text-4xl">✅</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Demande envoyée !</h2>
            <p className="text-slate-400 leading-relaxed">
              Votre inscription a bien été transmise à l'équipe{' '}
              <span className="text-emerald-400 font-semibold">{categoryName}</span>.
            </p>
            <p className="text-slate-500 text-sm mt-3">
              Vous serez contacté(e) une fois votre profil validé par le responsable.
            </p>
          </div>
        ) : (
          /* ── Registration form ────────────────────────────────── */
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-b border-[#2D3139] p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center border border-indigo-500/30">
                  <UserPlus className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white">Rejoindre l'équipe</h1>
                  <p className="text-indigo-300 text-sm">📋 {categoryName}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
                  <span>⚠️</span> {error}
                </div>
              )}

              {/* Photo (optional) */}
              <div className="flex justify-center">
                <div className="relative">
                  <div
                    className="w-20 h-20 rounded-full border-2 border-dashed border-[#2D3139] hover:border-indigo-500/50 bg-[#0A0B0E] flex items-center justify-center cursor-pointer overflow-hidden transition-all group"
                    onClick={() => photoRef.current?.click()}
                  >
                    {photo ? (
                      <img src={photo} alt="Photo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-slate-600 group-hover:text-indigo-400 transition-colors">
                        <Camera className="w-6 h-6" />
                        <span className="text-[10px] font-medium">Photo</span>
                      </div>
                    )}
                  </div>
                  {photo && (
                    <button
                      onClick={() => setPhoto('')}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  )}
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                </div>
              </div>
              <p className="text-center text-[11px] text-slate-600 -mt-2">Photo facultative</p>

              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                  Nom complet <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  autoFocus
                  placeholder="Prénom Nom"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-all placeholder-slate-600"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                  Téléphone <span className="text-red-400">*</span>
                </label>
                <input
                  type="tel"
                  placeholder="+216 XX XXX XXX"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
                  Email <span className="text-slate-600 normal-case font-normal">(facultatif)</span>
                </label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600"
                />
              </div>

              {/* Custom Fields */}
              {customFieldDefs.length > 0 && (
                <div className="pt-2 border-t border-[#2D3139]/50">
                  <label className="block text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider">
                    Informations complémentaires
                  </label>
                  <div className="space-y-4">
                    {customFieldDefs.map((field: any) => (
                      <div key={field.id} className="w-full">
                        <label className="block text-xs font-semibold text-slate-400 mb-2">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </label>
                        {field.field_type === 'textbox' && (
                          <input type="text"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600"
                            placeholder={field.label}
                            value={customFieldValues[field.id] || ''}
                            onChange={e => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          />
                        )}
                        {field.field_type === 'number' && (
                          <input type="number"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600"
                            placeholder={field.label}
                            value={customFieldValues[field.id] || ''}
                            onChange={e => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          />
                        )}
                        {field.field_type === 'date' && (
                          <input type="date"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all"
                            value={customFieldValues[field.id] || ''}
                            onChange={e => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                          />
                        )}
                        {field.field_type === 'radio' && (
                          <div className="grid grid-cols-2 gap-2 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139]">
                            {(field.options || []).map((opt: string) => (
                              <label key={opt}
                                className={`flex items-center justify-center p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                                  customFieldValues[field.id] === opt
                                    ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400'
                                    : 'border-[#2D3139] text-slate-400 hover:border-slate-600'
                                }`}
                              >
                                <input type="radio"
                                  name={`cf_${field.id}`}
                                  value={opt}
                                  checked={customFieldValues[field.id] === opt}
                                  onChange={e => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                                  className="sr-only"
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        )}
                        {field.field_type === 'checkbox' && (
                          <label className="flex items-center gap-3 p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139] cursor-pointer hover:border-slate-700 transition-all">
                            <input type="checkbox"
                              checked={customFieldValues[field.id] === 'true'}
                              onChange={e => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.checked ? 'true' : 'false' }))}
                              className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-indigo-500 focus:ring-indigo-500/20"
                            />
                            <span className="text-sm text-slate-400">{field.label}</span>
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting || !name.trim() || !phone.trim()}
                className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Envoi...</>
                ) : (
                  <><UserPlus className="w-4 h-4" /> Envoyer ma demande</>
                )}
              </button>

              <p className="text-center text-slate-600 text-xs">
                🔒 Vos données sont sécurisées et accessibles uniquement à votre équipe.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
